import { useEffect } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQS = [
  {
    q: "Where can I sell diabetic test strips for cash?",
    a: "If you are in the Jacksonville, Florida area, you can contact Cash for Test Strips Jacksonville. Text a photo of your box to (904) 955-9646 or use the Get Started form. We review the brand, expiration date, and packaging, then let you know if the item is eligible and how the local meetup works."
  },
  {
    q: "Can I sell unused diabetic test strips?",
    a: "In most cases, yes. Eligibility depends on the specific product, expiration date, and packaging condition. Boxes must be unopened and factory-sealed with at least six months to expiration. Boxes labeled Medicaid or Medicare are not eligible. See the accepted supplies information for the current list."
  },
  {
    q: "How do I sell unused diabetic test strips?",
    a: "Step 1: Take a clear photo of the front and back of your box showing the brand, product code, and expiration date. Step 2: Send it to (904) 955-9646 by text, or submit through the Get Started form. Step 3: We reply with eligibility and next steps. Step 4: If it qualifies, we schedule a public, in-person meetup in the Jacksonville area."
  },
  {
    q: "Do you buy diabetic supplies in Jacksonville, Florida?",
    a: "Yes. We are a locally operated buyer based in Jacksonville, FL. We meet sellers in Jacksonville and nearby areas including Jacksonville Beach, Orange Park, Mandarin, Arlington, Southside, Riverside, San Marco, Ponte Vedra, St. Augustine, and Fernandina Beach for eligible in-person transactions."
  },
  {
    q: "How do I know if my diabetic test strips are eligible to sell?",
    a: "Eligibility depends on four things: the brand and product type, the printed expiration date (minimum six months remaining), the packaging condition (sealed and undamaged), and whether the box is marked for Medicaid or Medicare. The fastest way to know is to send us a photo — we will confirm before you make any trip."
  },
  {
    q: "Why do you buy diabetic supplies?",
    a: "Many people have extra test strips, CGM sensors, or insulin pump supplies due to prescription changes, switching systems, or no longer needing them. Rather than letting them expire, we purchase eligible items and help get them to people who need more affordable diabetic supplies in the local community."
  },
  {
    q: "How much will I get paid for my test strips?",
    a: "Value depends on brand, product type, expiration date, and packaging condition. Because the market changes, we quote each submission individually rather than publishing a fixed price list. Send us a photo and we will give you a specific figure for your exact supplies."
  },
  {
    q: "How does the pickup and payment process work?",
    a: "We meet in person at a safe, public location in the Jacksonville area — anywhere convenient like a coffee shop, grocery store parking lot, or gas station. We review the supplies on the spot and pay you same-day cash. No shipping, no waiting on checks."
  },
  {
    q: "Is it legal to sell diabetic test strips?",
    a: "Yes. It is legal to sell unopened, unexpired diabetic test strips and supplies that were not paid for by Medicare or Medicaid. We do not accept boxes marked with those programs."
  },
  {
    q: "What brands and products do you accept?",
    a: "We accept a range of diabetic supplies: Dexcom (G7 and G6 sensors and transmitters), FreeStyle Libre (Libre 3, 3 Plus, Libre 2, 14-Day), Omnipod (5, DASH, Classic), Medtronic (MiniMed pumps, Guardian sensors, reservoirs, Quick-Set, Mio Advanced), Tandem (Autosoft, t:slim), plus test strips from Accu-Chek, OneTouch, Contour Next, ReliOn, True Metrix, and FreeStyle. Insulin and injection supplies are reviewed case-by-case."
  }
];

export const FAQ = () => {
  // Inject FAQ schema for these visible FAQs
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-schema-homepage";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, []);

  return (
    <section id="faq" className="py-24 md:py-32 bg-[#F5F5F0]" data-testid="faq-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#1F2937] mb-4" style={{ fontFamily: "Manrope" }}>
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-[#4B5563] max-w-2xl mx-auto">
            Got questions? We&apos;ve got answers.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {FAQS.map((faq) => (
              <AccordionItem
                key={faq.q}
                value={faq.q}
                className="bg-white rounded-xl border border-gray-100 px-6 overflow-hidden"
                data-testid={`faq-${faq.q.substring(0, 20).replace(/\s/g, "-").toLowerCase()}`}
              >
                <AccordionTrigger className="text-left font-semibold text-[#1F2937] py-5 hover:no-underline" style={{ fontFamily: "Manrope" }}>
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#4B5563] pb-5 leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};
